// ════════════════════════════════════════════════════════
//  AccountPage.js  —  Mobile-friendly
// ════════════════════════════════════════════════════════
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Save, Camera, X } from 'lucide-react';
import Layout from '../components/Layout';
import { Btn, TextInput, Alert, PageHeader, Section } from '../components/UI';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, API_KEY } from '../config';

function ProfileAvatar({ username, size = 96 }) {
  const [photo, setPhoto] = useState(null);
  const [hover, setHover] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!username) return;
    const stored = localStorage.getItem(`profile_photo_${username}`);
    if (stored) setPhoto(stored);
  }, [username]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB.'); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 256; canvas.height = 256;
        const ctx = canvas.getContext('2d');
        const s = Math.min(img.width, img.height);
        ctx.drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, 256, 256);
        const resized = canvas.toDataURL('image/jpeg', 0.82);
        localStorage.setItem(`profile_photo_${username}`, resized);
        setPhoto(resized);
        setUploading(false);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    localStorage.removeItem(`profile_photo_${username}`);
    setPhoto(null);
  };

  return (
    <div style={{ position: 'relative', width: size, height: size, cursor: 'pointer', flexShrink: 0 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => fileRef.current?.click()}
    >
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: photo ? 'transparent' : 'linear-gradient(135deg, var(--blue) 0%, var(--teal) 100%)',
        boxShadow: hover ? '0 0 0 4px rgba(30,111,255,0.22)' : '0 0 0 2px rgba(30,111,255,0.10)',
        transition: 'box-shadow 0.2s',
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        {photo
          ? <img src={photo} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <User size={Math.round(size * 0.38)} color="#fff" />
        }
      </div>
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.45)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        opacity: hover ? 1 : 0, transition: 'opacity 0.2s', pointerEvents: 'none',
      }}>
        <Camera size={Math.round(size * 0.23)} color="#fff" />
        <span style={{ fontSize: size > 72 ? 10 : 9, color: '#fff', marginTop: 3, fontWeight: 600 }}>
          {uploading ? 'Saving…' : 'Change'}
        </span>
      </div>
      {photo && (
        <button onClick={handleRemove} style={{
          position: 'absolute', top: -2, right: -2, width: 22, height: 22, borderRadius: '50%',
          background: 'var(--red)', border: '2px solid #fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', opacity: hover ? 1 : 0, transition: 'opacity 0.2s', zIndex: 2,
        }}>
          <X size={11} color="#fff" />
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
    </div>
  );
}

export function AccountPage() {
  const { username } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', newPassword: '', repeatPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.newPassword || !form.repeatPassword) { setError('All fields are required.'); return; }
    if (form.newPassword !== form.repeatPassword) { setError('Passwords do not match.'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_URL}/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify({ username, name: form.name.trim(), password: form.newPassword.trim() }),
      });
      const data = await res.json();
      if (res.ok) { setSuccess(data.message || 'Profile updated successfully.'); setForm({ name: '', newPassword: '', repeatPassword: '' }); }
      else setError(data.message || 'Update failed.');
    } catch { setError('Cannot connect to server.'); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <PageHeader title="Edit Profile" subtitle="Update your display name and password." />
      <div style={{ maxWidth: 540, margin: '0 auto' }}>
        <div style={{
          background: 'linear-gradient(135deg, var(--navy) 0%, var(--navy-soft) 100%)',
          borderRadius: 20, padding: '22px 24px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
        }}>
          <ProfileAvatar username={username} size={68} />
          <div style={{ flex: 1, minWidth: 160 }}>
            <p style={{ fontSize: 18, fontWeight: 700, color: '#fff', fontFamily: "'DM Serif Display', serif" }}>{username}</p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Cytometrix Account</p>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 5 }}>Tap photo to update</p>
          </div>
        </div>

        <Section>
          {error && <div style={{ marginBottom: 18 }}><Alert type="error">{error}</Alert></div>}
          {success && <div style={{ marginBottom: 18 }}><Alert type="success">{success}</Alert></div>}

          <form onSubmit={handleUpdate} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 6 }}>Username (cannot be changed)</label>
              <div style={{ padding: '10px 14px', background: 'var(--gray-50)', border: '1.5px solid var(--gray-200)', borderRadius: 10, fontSize: 13, color: 'var(--gray-400)', fontFamily: "'JetBrains Mono', monospace" }}>{username}</div>
            </div>
            <TextInput label="Full Name" icon={User} value={form.name}
              onChange={e => set('name', e.target.value.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '))}
              placeholder="Dr. Jane Smith"
            />
            <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 16 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-700)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Lock size={13} color="var(--gray-400)" /> Change Password
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <TextInput label="New Password" icon={Lock} type="password" value={form.newPassword} onChange={e => set('newPassword', e.target.value)} placeholder="Minimum 8 characters" />
                <TextInput label="Repeat New Password" icon={Lock} type="password" value={form.repeatPassword} onChange={e => set('repeatPassword', e.target.value)} placeholder="Confirm new password" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, paddingTop: 2 }}>
              <Btn variant="secondary" type="button" onClick={() => navigate('/')} style={{ flex: 1 }}>Cancel</Btn>
              <Btn type="submit" loading={loading} icon={Save} style={{ flex: 2 }}>Save Changes</Btn>
            </div>
          </form>
        </Section>
      </div>
    </Layout>
  );
}

export default AccountPage;
